import React, { useState, useEffect } from 'react';
import { CheckCircle2, XCircle, DollarSign, Loader2, RefreshCw, AlertTriangle, CreditCard } from 'lucide-react';
import { createPaymentSession, completeRegistration } from '../../lib/payment/paymentHandler';
import { supabase } from '../../lib/supabase';

interface PaymentInstructionsProps {
  memberName: string;
  memberId: string;
  registeredMember: any;
  tournamentId: string;
  costConfig: {
    member_cost: number;
    non_member_cost: number;
    late_fee: number;
    registration_deadline: string | null;
    start_date: string;
  } | null;
  membershipFees: {
    student_fee: number;
    single_fee: number;
    family_fee: number;
  } | null;
  onComplete: () => void;
  onCancel: () => void;
  paymentId: string | null;
}

const handleCancel = (onCancel: () => void) => {
  // Navigate back to homepage
  window.location.href = '/';
  onCancel();
};

export default function PaymentInstructions({ 
  memberName, 
  memberId,
  registeredMember,
  tournamentId,
  costConfig,
  membershipFees,
  onComplete,
  onCancel,
  paymentId
}: PaymentInstructionsProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [memberStatus, setMemberStatus] = useState<'M' | 'NVM' | null>(null);
  const [sessionUrl, setSessionUrl] = useState<string | null>(null);
  const [isCreatingPayment, setIsCreatingPayment] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [costs, setCosts] = useState<{
    baseCost: number;
    membershipCost: number;
    lateFee: number;
    totalCost: number;
  }>({
    baseCost: 0,
    membershipCost: 0,
    lateFee: 0,
    totalCost: 0
  });

  // Load costs and member status
  useEffect(() => {
    const loadData = async () => {
      console.log('Loading payment data for:', {
        memberId,
        tournamentId,
        costConfig,
        membershipFees,
        registeredMember
      });

      try {
        // For new members, use registration data instead of fetching
        if (registeredMember?.isNewMember) {
          // For new members, use the intended status based on registration choices
          setMemberStatus(
            registeredMember.join2025 && registeredMember.isVietnamese ? 'M' :
            registeredMember.join2025 ? 'NVM' : null
          );
          
          if (!costConfig || !membershipFees) {
            throw new Error('Missing cost configuration');
          }

           // Check if registration is late
           const now = new Date();
           const deadline = costConfig.registration_deadline
             ? new Date(costConfig.registration_deadline + 'Z')
             : new Date(costConfig.start_date);

           const isLateRegistration = now > deadline;
           
           // Calculate costs for new member
           const baseCost = registeredMember.join2025
             ? costConfig.member_cost    // Give member rate if joining
             : costConfig.non_member_cost;
           
           const lateFee = isLateRegistration ? costConfig.late_fee : 0;
           
           // Calculate membership fee if joining
           let membershipCost = 0;
           if (registeredMember.join2025 && registeredMember.membershipType) {
             membershipCost = registeredMember.membershipType === 'student' ? membershipFees.student_fee :
                             registeredMember.membershipType === 'single' ? membershipFees.single_fee :
                             registeredMember.membershipType === 'family' ? membershipFees.family_fee : 0;
           }

           setCosts({
             baseCost,
             membershipCost,
             lateFee,
             totalCost: baseCost + membershipCost + lateFee
           });
           
           setIsLoading(false);
         } else {
           // For existing members, fetch current status
           const { data: member, error: memberError } = await supabase
             .from('members')
             .select('current_status, season')
             .eq('id', memberId)
             .limit(1)
             .maybeSingle();

           if (memberError && !memberError.message?.includes('No rows found')) {
             throw memberError;
           }

           // Set member status from database or registration data
           setMemberStatus(
             member?.current_status ||
             (registeredMember?.join2025 && registeredMember?.isVietnamese ? 'M' :
              registeredMember?.join2025 ? 'NVM' : null)
           );
           
           if (!costConfig || !membershipFees) {
             throw new Error('Missing cost configuration');
           }

           // Check if registration is late 
           const now = new Date();
           const deadline = costConfig.registration_deadline
             ? new Date(costConfig.registration_deadline + 'Z')
             : new Date(costConfig.start_date);

           const isLateRegistration = now > deadline;

           // Calculate base tournament cost
           const baseCost = member?.current_status === 'M' ||
                            member?.current_status === 'NVM' ||
                            registeredMember?.join2025 // Also give member rate if joining
             ? costConfig.member_cost
             : costConfig.non_member_cost;

           // Calculate late fee if applicable
           const lateFee = isLateRegistration ? costConfig.late_fee : 0;

           // Get membership cost if joining
           let membershipCost = 0;
           if (registeredMember?.join2025 && registeredMember?.membershipType) {
             membershipCost = registeredMember.membershipType === 'student' ? membershipFees.student_fee :
                             registeredMember.membershipType === 'single' ? membershipFees.single_fee :
                             registeredMember.membershipType === 'family' ? membershipFees.family_fee : 0;
           }

           setCosts({
             baseCost,
             membershipCost,
             lateFee,
             totalCost: baseCost + membershipCost + lateFee
           });

           setIsLoading(false);
         }
       } catch (error) {
         console.error('Error loading payment data:', error);
         setError(error instanceof Error ? error.message : 'Failed to load payment data');
         setIsLoading(false);
       }
     };

    // Get payment URL if we have a payment ID
    const getPaymentUrl = async () => {
      if (paymentId) {
        try {
          const { data: payment } = await supabase
            .from('tournament_payments')
            .select('stripe_session_url')
            .eq('id', paymentId)
            .single();

          if (payment?.stripe_session_url) {
            setSessionUrl(payment.stripe_session_url);
          }
        } catch (error) {
          console.error('Error getting payment URL:', error);
        }
      }
    };

     loadData();
    getPaymentUrl();
   }, [tournamentId, memberId, paymentId, costConfig, membershipFees, registeredMember]);

  if (isLoading || !costConfig || !membershipFees) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 text-red-600 p-4 rounded-md">
          <XCircle className="w-5 h-5 mb-2" />
          <p>{error}</p>
        </div>
        <div className="mt-4 flex justify-end gap-3">
          <button
            onClick={() => handleCancel(onCancel)}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Back
          </button>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const handlePayment = async () => {
    if (isCreatingPayment) return;
    setIsCreatingPayment(true);
    setError(null);

    // Log cost breakdown before creating session
    console.log('Cost breakdown:', {
      baseCost: costs.baseCost,
      membershipCost: costs.membershipCost,
      lateFee: costs.lateFee,
      totalCost: costs.totalCost,
      memberStatus,
      registeredMember
    });
    
    try {
      // Create payment session with direct link
      const { success, url, error: paymentError } = await createPaymentSession({
        memberId,
        tournamentId,
        amount: costs.totalCost,
        isMember: memberStatus === 'M' || memberStatus === 'NVM',
        isLate: costs.lateFee > 0,
        membershipType: registeredMember?.membershipType,
        notes: registeredMember?.notes
      });

      if (!success || !url) {
        throw new Error(paymentError || 'Failed to create payment session');
      }

      // Store registration details
      localStorage.setItem('pending_registration', JSON.stringify({
        memberId,
        tournamentId,
        amount: costs.totalCost,
        timestamp: Date.now()
      }));

      // Redirect to payment URL
      window.location.href = url;
    } catch (error: any) {
      console.error('Payment error:', error);
      setError(error instanceof Error ? error.message : 'Failed to process payment');
    } finally {
      setIsCreatingPayment(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <h2 className="text-xl font-semibold mb-6">Tournament Registration Fees</h2>

      {/* Cost Breakdown */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="space-y-4">
          {/* Cost Breakdown */}
          <div className="text-sm text-gray-500 mb-4">
            <div>Cost Breakdown:</div>
            <div>Base Registration: ${(costs.baseCost / 100).toFixed(2)}</div>
            {costs.membershipCost > 0 && (
              <div>Membership Fee: ${(costs.membershipCost / 100).toFixed(2)}</div>
            )}
            {costs.lateFee > 0 && (
              <div>Late Fee: ${(costs.lateFee / 100).toFixed(2)}</div>
            )}
          </div>

          {/* Member Status */}
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-600">Your Status</span>
            <span className={`px-2 py-1 rounded-full ${
              memberStatus === 'M' || memberStatus === 'NVM' ? 'bg-green-100 text-green-800' :
              memberStatus === 'NVM' ? 'bg-blue-100 text-blue-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {memberStatus === 'M' ? 'Member' :
               memberStatus === 'NVM' ? 'Non-Voting Member' :
               'Non-Member'}
            </span>
          </div>

          {/* Base Cost */}
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Base Registration Fee</span>
            <span className="font-medium">
              ${(costs.baseCost / 100).toFixed(2)}
            </span>
          </div>

          {/* Late Fee if applicable */}
          {costs.lateFee > 0 && (
            <div className="flex justify-between items-center text-amber-600">
              <span>Late Registration Fee</span>
              <span className="font-medium">${(costs.lateFee / 100).toFixed(2)}</span>
            </div>
          )}

          {/* Membership Cost if applicable */}
          {costs.membershipCost > 0 && (
            <div className="flex justify-between items-center text-primary-600">
              <span>2025 Membership Fee</span>
              <span className="font-medium">${(costs.membershipCost / 100).toFixed(2)}</span>
            </div>
          )}

          {/* Total */}
          <div className="border-t pt-4 flex justify-between items-center">
            <span className="font-medium">Total Due</span>
            <span className="text-lg font-bold">${(costs.totalCost / 100).toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        <button
          onClick={() => handleCancel(onCancel)}
          className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handlePayment}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center gap-2 transition-colors"
          disabled={isCreatingPayment}
        >
          <CreditCard className="w-5 h-5" />
          {isCreatingPayment ? 'Creating Payment...' : 'Pay Now'}
        </button>
      </div>

      {/* Payment Notes */}
      <div className="mt-6 text-xs text-gray-500 space-y-1">
        <p className="font-medium">Important Notes:</p>
        <ul className="list-disc list-inside space-y-0.5">
          <li>Payment session expires in 30 minutes</li>
          <li>Late registration fee applies after registration deadline</li>
          <li>No refunds available</li>
          <li>Registration will be confirmed automatically after payment</li>
        </ul>
      </div>
    </div>
  );
}

export { PaymentInstructions }